import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { Translator } from "@sharedV11/services/translator/translator.service";
import { SnackbarInfoService } from "@sharedV11/services/alerts/snackbar-info/snackbar-info.service";

export interface AccionPermiso {
  AccionId: number;
  NombreAccion: string;
  Activo: number;
}

export interface ModuloPermiso {
  ModuloId: number;
  ModuloPadreId?: number;
  NombreModulo: string;
  Acciones: AccionPermiso[];
}

export interface AplicacionPermiso {
  AplicacionId: number;
  NombreAplicacion: string;
  Modulos: ModuloPermiso[];
}

export interface TipoUsuarioPermisos {
  pkId: number | string;
  Nombre: string;
  Descripcion?: string;
  CentrosIds?: string;
  Permisos?: {
    Aplicaciones: AplicacionPermiso[];
  };
}

const ACCIONES_CUATRO = ["ver", "nuevo", "modificar", "eliminar"] as const;

/** App slice from parent (in-memory edit): same shape as AplicacionPermiso */
export type AplicacionPermisoSlice = AplicacionPermiso;

@Component({
  selector: "app-tipo-usuario-permisos-dialog",
  templateUrl: "./tipo-usuario-permisos-dialog.component.html",
  styleUrls: ["./tipo-usuario-permisos-dialog.component.scss"],
})
export class TipoUsuarioPermisosDialogComponent implements OnInit, OnChanges {
  /** In-memory mode: parent passes the app object and the main module id to edit */
  @Input() aplicacion: AplicacionPermisoSlice | null = null;
  @Input() moduloId: number | null = null;
  @Input() translation: any;
  /** True cuando se está creando un nuevo perfil (no editando). Usado para mostrar "Activar todos". */
  @Input() isNewProfile = false;
  @Output() saveRequested = new EventEmitter<void>();
  @Output() cancelRequested = new EventEmitter<void>();

  loading = false;
  saving = false;
  t: any = {};

  /** Cópia do estado inicial (Activo por AccionId) ao abrir el modal; se restaura al cerrar con X. */
  private initialAccionesSnapshot: Map<number, Map<number, number>> = new Map();
  /** True si al abrir el modal ya había al menos un permiso activo (para mantener el botón visible en el ciclo Activar/Desactivar). */
  private hadAtLeastOneActivoWhenOpened = false;

  constructor(
    private translator: Translator,
    private snackbarService: SnackbarInfoService
  ) {}

  ngOnInit(): void {
    this.t = this.translation ?? this.translator?.GetTranslations?.() ?? {};
    if (this.aplicacion && this.moduloId != null) {
      this.captureInitialSnapshot();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["translation"] && !changes["translation"].firstChange) {
      this.t = changes["translation"].currentValue ?? this.translator?.GetTranslations?.() ?? {};
    }
    if ((changes["aplicacion"] || changes["moduloId"]) && this.aplicacion && this.moduloId != null) {
      this.captureInitialSnapshot();
    }
  }

  label(key: string, fallback?: string): string {
    const v = this.t?.[key];
    return v !== undefined && v !== null && v !== "" ? v : fallback ?? key;
  }

  /**
   * Módulo que se está editando en el desglose.
   * Primero busca un módulo principal (ModuloPadreId = 0) con ese ModuloId; si no existe
   * (ej. KOUROS: todos tienen ModuloPadreId 183 y el 183 no viene en la lista), usa el módulo
   * con ese ModuloId para mostrar sus acciones igualmente.
   */
  get moduloPrincipal(): ModuloPermiso | null {
    if (!this.aplicacion || this.moduloId == null) return null;
    const modulos = this.aplicacion.Modulos || [];
    const principal = modulos.find(
      (m) => Number(m.ModuloId) === Number(this.moduloId) && this.isModuloPrincipal(m)
    );
    if (principal) return principal;
    return modulos.find((m) => Number(m.ModuloId) === Number(this.moduloId)) ?? null;
  }

  /** Submódulos del módulo principal */
  get submodulos(): ModuloPermiso[] {
    if (!this.aplicacion || this.moduloId == null) return [];
    return (this.aplicacion.Modulos || []).filter(
      (m) => Number(m.ModuloPadreId) === Number(this.moduloId)
    );
  }

  /** Nombre de la acción "padre" que controla el acceso a todo el módulo (ej. InicioAcceso en TAQUILLA). */
  private static readonly NOMBRE_ACCION_PADRE = "inicioacceso";

  /** Acción "padre" del módulo principal (InicioAcceso, etc.): si está inactiva, el resto no se puede activar. */
  get accionPadre(): AccionPermiso | null {
    const principal = this.moduloPrincipal;
    if (!principal) return null;
    return (principal.Acciones || []).find(
      (a) => (a.NombreAccion || "").trim().toLowerCase() === TipoUsuarioPermisosDialogComponent.NOMBRE_ACCION_PADRE
    ) ?? null;
  }

  /** True si no hay acción padre o si está activa (permite activar el resto). */
  get isPadreActivo(): boolean {
    const padre = this.accionPadre;
    return !padre || padre.Activo === 1;
  }

  private isAccionPadre(acc: AccionPermiso): boolean {
    return (acc?.NombreAccion || "").trim().toLowerCase() === TipoUsuarioPermisosDialogComponent.NOMBRE_ACCION_PADRE;
  }

  get hasData(): boolean {
    return !!this.aplicacion && this.moduloPrincipal != null;
  }

  /** True si el módulo principal o algún submódulo tiene al menos una acción con Activo=1 (vino así de la API). */
  get hasAtLeastOneActivoInCurrentModule(): boolean {
    const principal = this.moduloPrincipal;
    if (!principal) return false;
    if ((principal.Acciones || []).some((a) => a.Activo === 1)) return true;
    for (const sub of this.submodulos) {
      if ((sub.Acciones || []).some((a) => a.Activo === 1)) return true;
    }
    return false;
  }

  /** Mostrar botón Activar/Desactivar todos al crear nuevo perfil si al abrir ya había al menos un permiso activo; se mantiene visible en el ciclo. */
  get showActivarTodosButton(): boolean {
    return this.isNewProfile && this.hadAtLeastOneActivoWhenOpened;
  }

  /** True si todas las acciones del módulo principal y submódulos están con Activo=1. */
  get allActivosActivosInCurrentModule(): boolean {
    const principal = this.moduloPrincipal;
    if (!principal) return false;
    const allOn = (mod: ModuloPermiso) => (mod.Acciones || []).length > 0 && (mod.Acciones || []).every((a) => a.Activo === 1);
    if (!allOn(principal)) return false;
    for (const sub of this.submodulos) {
      if (!allOn(sub)) return false;
    }
    return true;
  }

  /** Etiqueta del botón: "Desactivar todos" si todos están activos, si no "Activar todos". */
  get activarTodosButtonLabel(): string {
    return this.allActivosActivosInCurrentModule
      ? this.label('DesactivarTodos', 'Desactivar todos')
      : this.label('ActivarTodos', 'Activar todos');
  }

  private setAllAccionesOn(mod: ModuloPermiso): void {
    for (const a of mod?.Acciones || []) {
      a.Activo = 1;
    }
  }

  /** Activa o desactiva todos según el estado actual; no guarda hasta que el usuario pulse Guardar. */
  toggleActivarDesactivarTodos(): void {
    if (this.allActivosActivosInCurrentModule) {
      this.desactivarTodos();
    } else {
      this.activarTodos();
    }
  }

  /** Activa todos los permisos del módulo principal y sus submódulos (solo en memoria; Guardar persiste). */
  activarTodos(): void {
    const principal = this.moduloPrincipal;
    if (!principal || !this.aplicacion) return;
    this.setAllAccionesOn(principal);
    for (const sub of this.submodulos) {
      this.setAllAccionesOn(sub);
    }
  }

  /** Desactiva todos los permisos del módulo principal y sus submódulos (solo en memoria). */
  desactivarTodos(): void {
    const principal = this.moduloPrincipal;
    if (!principal || !this.aplicacion) return;
    this.setAllAccionesOff(principal);
    for (const sub of this.submodulos) {
      this.setAllAccionesOff(sub);
    }
  }

  private captureInitialSnapshot(): void {
    this.initialAccionesSnapshot.clear();
    this.hadAtLeastOneActivoWhenOpened = false;
    const principal = this.moduloPrincipal;
    if (!principal) return;
    this.hadAtLeastOneActivoWhenOpened = this.hasAtLeastOneActivoInCurrentModule;
    const saveMod = (mod: ModuloPermiso) => {
      const byAccion = new Map<number, number>();
      for (const a of mod?.Acciones || []) {
        byAccion.set(Number(a.AccionId), a.Activo);
      }
      this.initialAccionesSnapshot.set(mod.ModuloId, byAccion);
    };
    saveMod(principal);
    for (const sub of this.submodulos) saveMod(sub);
  }

  private restoreInitialSnapshot(): void {
    const modulos = this.aplicacion?.Modulos || [];
    this.initialAccionesSnapshot.forEach((byAccion, moduloId) => {
      const mod = modulos.find((m) => Number(m.ModuloId) === Number(moduloId));
      if (!mod) return;
      for (const a of mod.Acciones || []) {
        const activo = byAccion.get(Number(a.AccionId));
        if (activo !== undefined) a.Activo = activo;
      }
    });
  }

  /**
   * Retorna sempre as quatro ações (Ver, Nuevo, Modificar, Eliminar) para exibir no modal.
   * Se a API não devolver alguma delas no módulo, usa um placeholder com Activo=0 e AccionId=0 (não enviado no save).
   */
  getAccionesCuatro(mod: ModuloPermiso): AccionPermiso[] {
    const acciones = mod.Acciones || [];
    const result: AccionPermiso[] = [];
    for (const nombre of ACCIONES_CUATRO) {
      const acc = acciones.find(
        (a) => (a.NombreAccion || "").trim().toLowerCase() === nombre
      );
      if (acc) {
        result.push(acc);
      } else {
        result.push({
          AccionId: 0,
          NombreAccion: nombre.charAt(0).toUpperCase() + nombre.slice(1),
          Activo: 0,
        });
      }
    }
    return result;
  }

  /**
   * Ações a mostrar no modal: sempre as originais da API quando o módulo tiver ações
   * (CIERRE, INICIO, VENTA, MODIFICAR RESERVA, etc. — cada um com os seus NombreAccion reais).
   * Só usa os 4 placeholders (Ver/Nuevo/Modificar/Eliminar) quando o módulo não tiver nenhuma ação.
   * Assim não aparecem checks desativados a mais para módulos com nomes diferentes.
   */
  getAccionesParaMostrar(mod: ModuloPermiso): AccionPermiso[] {
    const acciones = mod.Acciones || [];
    if (acciones.length > 0) return acciones;
    return this.getAccionesCuatro(mod);
  }

  /** Acción "Ver" si existe en el módulo (para mostrarla primero y destacada). */
  getAccionVer(mod: ModuloPermiso): AccionPermiso | null {
    const list = this.getAccionesParaMostrar(mod);
    return list.find((a) => (a.NombreAccion || "").trim().toLowerCase() === "ver") ?? null;
  }

  /** Resto de acciones del módulo (sin "Ver"), para mostrar después del toggle Ver. */
  getAccionesResto(mod: ModuloPermiso): AccionPermiso[] {
    const list = this.getAccionesParaMostrar(mod);
    return list.filter((a) => (a.NombreAccion || "").trim().toLowerCase() !== "ver");
  }

  /** Acciones del módulo excluyendo la acción padre (InicioAcceso), para mostrarlas en grid. */
  getAccionesSinPadre(mod: ModuloPermiso): AccionPermiso[] {
    const padre = this.accionPadre;
    if (!padre) return this.getAccionesParaMostrar(mod);
    return this.getAccionesParaMostrar(mod).filter(
      (a) => a.AccionId !== padre.AccionId && (a.NombreAccion || "").trim().toLowerCase() !== TipoUsuarioPermisosDialogComponent.NOMBRE_ACCION_PADRE
    );
  }

  /** Texto de ayuda para la acción padre del módulo: sin ella activa, el resto no tiene efecto. */
  getPadreHint(): string {
    return this.label(
      "PermisoPadreActivaAcceso",
      "Activa el acceso a la aplicación; sin este permiso el resto no tendrá efecto."
    );
  }

  /** Texto de ayuda bajo el toggle Ver: distinto si el módulo solo tiene Ver o tiene más acciones. */
  getVerHint(mod: ModuloPermiso): string {
    const hasResto = this.getAccionesResto(mod).length > 0;
    return hasResto
      ? this.label('VerActivaDemas', 'Activa el acceso y desbloquea las demás acciones')
      : this.label('VerSoloAcceso', 'Activa el acceso a este módulo');
  }

  labelAccion(nombreAccion: string): string {
    const n = (nombreAccion || "").trim().toLowerCase();
    if (n === "ver") return this.label("Ver", "Ver");
    if (n === "nuevo") return this.label("Nuevo", "Nuevo");
    if (n === "modificar") return this.label("Modificar", "Modificar");
    if (n === "eliminar") return this.label("Eliminar", "Eliminar");
    return nombreAccion || "";
  }

  private isVerAccion(acc: AccionPermiso): boolean {
    return (acc?.NombreAccion || "").trim().toLowerCase() === "ver";
  }

  isModuloPrincipal(mod: ModuloPermiso): boolean {
    return !mod?.ModuloPadreId || mod.ModuloPadreId === 0;
  }

  getModulosPrincipales(app: AplicacionPermiso): ModuloPermiso[] {
    return (app?.Modulos || []).filter((m) => this.isModuloPrincipal(m));
  }

  getModulosParaMostrar(app: AplicacionPermiso): ModuloPermiso[] {
    const principales = this.getModulosPrincipales(app);
    if (principales.length > 0) return principales;
    return app?.Modulos ?? [];
  }

  getSubmodulos(app: AplicacionPermiso, moduloPadreId: number): ModuloPermiso[] {
    return (app?.Modulos || []).filter((m) => Number(m.ModuloPadreId) === Number(moduloPadreId));
  }

  isVerOff(mod: ModuloPermiso): boolean {
    const ver = (mod?.Acciones || []).find((a) => (a?.NombreAccion || "").trim().toLowerCase() === "ver");
    return ver ? ver.Activo !== 1 : false;
  }

  isToggleDisabled(mod: ModuloPermiso, acc: AccionPermiso): boolean {
    // Placeholders (AccionId 0) não existem na API — não permitir alterar.
    if (!acc.AccionId || acc.AccionId === 0) return true;
    // Si hay acción "padre" (InicioAcceso) inactiva: no se puede activar nada que dependa de ella.
    if (this.accionPadre && !this.isPadreActivo) {
      if (this.isModuloPrincipal(mod)) {
        return !this.isAccionPadre(acc);
      }
      return true;
    }
    // Se VER estiver desligado, bloquear Nuevo/Modificar/Eliminar para evitar inconsistência.
    return this.isVerOff(mod) && !this.isVerAccion(acc);
  }

  /** Número de ações ativas em um módulo (para mostrar no header da sanfona). */
  getActiveAccionesCount(mod: ModuloPermiso): number {
    return (mod?.Acciones || []).filter((a) => a.Activo === 1).length;
  }

  /** Total de ações relevantes em um módulo (usa getAccionesParaMostrar para ser consistente). */
  getTotalAccionesCount(mod: ModuloPermiso): number {
    return this.getAccionesParaMostrar(mod).length;
  }

  /**
   * Quando o utilizador abre um submódulo, garantir que o cabeçalho azul
   * fique visível no viewport do modal (alinhado logo abaixo do topo).
   * Aceita tanto o próprio header (HTMLElement) como o componente Angular
   * (MatExpansionPanelHeader), extraindo o nativeElement em tempo de execução.
   */
  onSubmoduloOpened(headerRef: any): void {
    // Resolver para HTMLElement, seja vindo como elemento nativo ou componente Angular.
    const headerEl: HTMLElement | null =
      headerRef instanceof HTMLElement
        ? headerRef
        : headerRef?._elementRef?.nativeElement ?? null;

    if (!headerEl) {
      return;
    }

    // Espera um tick para o layout aplicar a altura expandida antes de rolar.
    setTimeout(() => {
      // Sobe na hierarquia até encontrar o contêiner com overflow (form-body).
      let scrollContainer: HTMLElement | null = headerEl.parentElement;
      while (scrollContainer && !scrollContainer.classList.contains("form-body")) {
        scrollContainer = scrollContainer.parentElement;
      }

      const container = scrollContainer ?? headerEl;
      const containerRect = container.getBoundingClientRect();
      const headerRect = headerEl.getBoundingClientRect();

      const topMargin = 16; // margem para não colar no topo
      const delta = headerRect.top - containerRect.top - topMargin;

      if (Math.abs(delta) > 4) {
        container.scrollBy({
          top: delta,
          left: 0,
          behavior: "smooth",
        });
      }
    }, 0);
  }

  private setAllAccionesOff(mod: ModuloPermiso): void {
    for (const a of mod?.Acciones || []) {
      a.Activo = 0;
    }
  }

  onAccionChange(app: AplicacionPermiso, mod: ModuloPermiso, acc: AccionPermiso, value: boolean): void {
    const isVer = this.isVerAccion(acc);
    const isPadre = this.isAccionPadre(acc);

    if (isPadre && !value) {
      this.setAccionActivo(acc, false);
      this.setAllAccionesOff(mod);
      for (const sub of this.getSubmodulos(app, mod.ModuloId)) {
        this.setAllAccionesOff(sub);
      }
      return;
    }

    if (isVer && !value) {
      this.setAllAccionesOff(mod);
      if (this.isModuloPrincipal(mod)) {
        for (const sub of this.getSubmodulos(app, mod.ModuloId)) {
          this.setAllAccionesOff(sub);
        }
      }
      return;
    }

    this.setAccionActivo(acc, value);
  }

  setAccionActivo(accion: AccionPermiso, value: boolean): void {
    accion.Activo = value ? 1 : 0;
  }

  isAccionActivo(accion: AccionPermiso): boolean {
    return accion.Activo === 1;
  }

  onSave(): void {
    if (!this.aplicacion || this.saving) return;
    this.saving = true;
    this.snackbarService.openSnackBar(
      this.label("GuardadoCorrectamente", "Guardado correctamente"),
      "",
      "green-snackbar"
    );
    this.saveRequested.emit();
    this.saving = false;
  }

  onCancel(): void {
    this.restoreInitialSnapshot();
    this.cancelRequested.emit();
  }
}
